import { useEffect, useState } from "react";
import api from "../api/axios";
import { runFetcherJob, runGoogleAdsFetcherJob, runPushToSheetsJob, runFakturowniaJob } from "../api/adminJobs";

type User = {
  id: number;
  email: string;
  role: string;
  created_at: string;
  is_active: boolean;
};

const DashboardAdmin = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [adminJobOutput, setAdminJobOutput] = useState<string | null>(null);
  const [adminJobError, setAdminJobError] = useState<string | null>(null);
  const [adminJobLoading, setAdminJobLoading] = useState<string | null>(null);
  const [userActionInfo, setUserActionInfo] = useState<string | null>(null);
  const [userActionError, setUserActionError] = useState<string | null>(null);
  const [selectedJob, setSelectedJob] = useState<"fetcher" | "google_ads_fetcher" | "push_to_sheets" | "fakturownia">("fetcher");
  const [jobLogLoading, setJobLogLoading] = useState(false);
  const [jobLogError, setJobLogError] = useState<string | null>(null);

  // Pobierz userów
  useEffect(() => {
    (async () => {
      setLoading(true);
      const res = await api.get("/users");
      setUsers(res.data);
      setLoading(false);
    })();
  }, []);

  // Aktywacja/dezaktywacja usera
  const handleToggleActive = async (user: User) => {
    setUserActionInfo(null);
    setUserActionError(null);
    try {
      await api.patch(`/users/${user.id}/activate`, null, {
        params: { is_active: !user.is_active }
      });
      setUsers(us => us.map(u => u.id === user.id ? { ...u, is_active: !u.is_active } : u));
      setUserActionInfo(`Status użytkownika ${user.email} zmieniony.`);
    } catch {
      setUserActionError("Błąd zmiany statusu usera!");
    }
  };


  // Zmiana roli użytkownika
  const handleToggleRole = async (user: User) => {
    setUserActionInfo(null);
    setUserActionError(null);
    const newRole = user.role === "admin" ? "user" : "admin";
    if (!window.confirm(`Na pewno zmienić rolę użytkownika ${user.email} na ${newRole}?`)) return;
    try {
      await api.patch(`/users/${user.id}/role`, { role: newRole });
      setUsers(us => us.map(u => u.id === user.id ? { ...u, role: newRole } : u));
      setUserActionInfo(`Rola użytkownika ${user.email} zmieniona na ${newRole}.`);
    } catch (e: any) {
      setUserActionError(e?.response?.data?.detail || "Błąd zmiany roli użytkownika!");
    }
  };

  // Usunięcie usera
  const handleDelete = async (user: User) => {
    setUserActionInfo(null);
    setUserActionError(null);
    if (!window.confirm(`Na pewno usunąć użytkownika ${user.email}?`)) return;
    try {
      await api.delete(`/users/${user.id}`);
      setUsers(us => us.filter(u => u.id !== user.id));
      setUserActionInfo(`Użytkownik ${user.email} usunięty.`);
    } catch (e: any) {
      setUserActionError(e?.response?.data?.detail || "Błąd usuwania usera!");
    }
  };


  // admin joby
  const handleAdminJob = async (
    type: "fetcher" | "google_ads_fetcher_standard" | "sheets" | "fakturownia"
  ) => {
    setAdminJobError(null);
    setAdminJobOutput(null);
    setAdminJobLoading(type);
    try {
      let res;
      if (type === "fetcher") res = await runFetcherJob();
      else if (type === "google_ads_fetcher_standard") res = await runGoogleAdsFetcherJob("standard");
      else if (type === "sheets") res = await runPushToSheetsJob();
      else res = await runFakturowniaJob();
      setAdminJobOutput(res.data.output || "Zadanie uruchomione!");
    } catch (e: any) {
      setAdminJobError(
        e?.response?.data?.detail ||
        e?.message ||
        "Błąd uruchamiania joba!"
      );
    } finally {
      setAdminJobLoading(null);
    }
  };

  const handleDownloadJobLog = async () => {
  setJobLogError(null);
  setJobLogLoading(true);
  try {
    const res = await api.get(`/admin/logs/last`, {
      params: { job: selectedJob },
      responseType: "blob", // <<< ważne
    });

    // Spróbuj wyciągnąć nazwę pliku z nagłówka Content-Disposition
    const dispo = res.headers["content-disposition"] || res.headers["Content-Disposition"];
    let filename = `log_${selectedJob}.log`;
    if (dispo) {
      const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(dispo);
      if (match) filename = decodeURIComponent(match[1] || match[2]);
    }

    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (e: any) {
    setJobLogError(e?.response?.data?.detail || "Nie udało się pobrać loga.");
  } finally {
    setJobLogLoading(false);
  }
};

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: 20 }}>
      <div style={{ background: "#fff3cd", border: "1px solid #ffc107", borderRadius: 6, padding: 12, marginBottom: 24 }}>
        <h2 style={{ margin: "0 0 8px 0", color: "#856404" }}>⚠️ Panel administratora</h2>
        <p style={{ margin: 0, color: "#856404", fontSize: 14 }}>
          Nie uruchamiaj zadań bez znajomości ich działania! Operacje mogą wpływać na dane produkcyjne.
        </p>
      </div>

      {userActionInfo && (
        <div style={{ color: "#117e40", background: "#f0fff0", padding: 12, border: "1.5px solid #0d2", borderRadius: 6, marginBottom: 16 }}>
          ✅ {userActionInfo}
        </div>
      )}
      {userActionError && (
        <div style={{ color: "#a00", background: "#fff0f0", padding: 12, border: "1.5px solid #d22", borderRadius: 6, marginBottom: 16 }}>
          ❌ {userActionError}
        </div>
      )}

      {/* Sekcja pobierania logów */}
      <div style={{ marginBottom: 32, padding: 16, border: "1px solid #ddd", borderRadius: 6, background: "#f9f9f9" }}>
        <h3 style={{ marginTop: 0, marginBottom: 12 }}>📄 Pobierz logi zadań</h3>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <label style={{ flex: 1 }}>
            <span style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 500 }}>Wybierz zadanie:</span>
            <select
              value={selectedJob}
              onChange={e => setSelectedJob(e.target.value as any)}
              style={{ width: "100%", padding: "8px 12px", borderRadius: 4, border: "1px solid #ccc" }}
            >
              <option value="fetcher">Allegro Ads Fetcher</option>
              <option value="google_ads_fetcher">Google Ads Fetcher</option>
              <option value="push_to_sheets">Push to Sheets</option>
              <option value="fakturownia">Fakturownia</option>
            </select>
          </label>
          <button
            onClick={handleDownloadJobLog}
            disabled={jobLogLoading}
            style={{
              padding: "10px 20px",
              background: jobLogLoading ? "#ccc" : "#2196F3",
              color: "white",
              border: "none",
              borderRadius: 4,
              cursor: jobLogLoading ? "not-allowed" : "pointer",
              fontWeight: 500,
              marginTop: 22
            }}
          >
            {jobLogLoading ? "⏳ Pobieram..." : "📥 Pobierz log"}
          </button>
        </div>
        {jobLogError && (
          <div style={{ color: "#c00", marginTop: 12, fontSize: 14 }}>❌ {jobLogError}</div>
        )}
      </div>
      <div style={{ marginBottom: 22 }}>
        <h3 style={{ marginBottom: 12 }}>🔄 Zadania synchronizacji</h3>

        {/* Allegro Ads */}
        <div style={{ marginBottom: 16, padding: 12, border: "1px solid #ddd", borderRadius: 4, background: "#f9f9f9" }}>
          <strong>Allegro Ads</strong>
          <div style={{ marginTop: 8 }}>
            <button
              onClick={() => handleAdminJob("fetcher")}
              disabled={adminJobLoading !== null}
              style={{
                padding: "8px 12px",
                background: adminJobLoading !== null ? "#ccc" : "#FF9800",
                color: "white",
                border: "none",
                borderRadius: 4,
                cursor: adminJobLoading !== null ? "not-allowed" : "pointer",
                fontWeight: 500
              }}
            >
              {adminJobLoading === "fetcher" ? "⏳ Uruchamiam..." : "📥 Pobierz dane (7 dni)"}
            </button>
          </div>
          <div style={{ fontSize: 12, color: "#ff9800", marginTop: 8, fontWeight: 500 }}>
            ⚠️ Zadanie może trwać kilka minut (~5-10 min). Nie zamykaj zakładki.
          </div>
        </div>

        {/* Google Ads */}
        <div style={{ marginBottom: 16, padding: 12, border: "1px solid #ddd", borderRadius: 4, background: "#f9f9f9" }}>
          <strong>Google Ads</strong>
          <div style={{ marginTop: 8 }}>
            <button
              onClick={() => handleAdminJob("google_ads_fetcher_standard")}
              disabled={adminJobLoading !== null}
              style={{
                padding: "8px 12px",
                background: adminJobLoading !== null ? "#ccc" : "#2196F3",
                color: "white",
                border: "none",
                borderRadius: 4,
                cursor: adminJobLoading !== null ? "not-allowed" : "pointer",
                fontWeight: 500
              }}
            >
              {adminJobLoading === "google_ads_fetcher_standard" ? "⏳ Uruchamiam..." : "📥 Pobierz dane (7 dni)"}
            </button>
          </div>
          <div style={{ fontSize: 12, color: "#666", marginTop: 6 }}>
            Pobiera statystyki z ostatnich 7 dni (używany w cronie codziennie)
          </div>
          <div style={{ fontSize: 12, color: "#ff9800", marginTop: 8, fontWeight: 500 }}>
            ⚠️ Zadanie może trwać kilka minut (~5-10 min). Nie zamykaj zakładki.
          </div>
        </div>

        {/* Google Sheets */}
        <div style={{ marginBottom: 16, padding: 12, border: "1px solid #ddd", borderRadius: 4, background: "#f9f9f9" }}>
          <strong>Google Sheets</strong>
          <div style={{ marginTop: 8 }}>
            <button
              onClick={() => handleAdminJob("sheets")}
              disabled={adminJobLoading !== null}
              style={{
                padding: "8px 12px",
                background: adminJobLoading !== null ? "#ccc" : "#4CAF50",
                color: "white",
                border: "none",
                borderRadius: 4,
                cursor: adminJobLoading !== null ? "not-allowed" : "pointer",
                fontWeight: 500
              }}
            >
              {adminJobLoading === "sheets" ? "⏳ Uruchamiam..." : "📊 Zaktualizuj arkusze"}
            </button>
          </div>
        </div>

        {/* Fakturownia */}
        <div style={{ marginBottom: 16, padding: 12, border: "1px solid #ddd", borderRadius: 4, background: "#f9f9f9" }}>
          <strong>Fakturownia</strong>
          <div style={{ marginTop: 8 }}>
            <button
              onClick={() => handleAdminJob("fakturownia")}
              disabled={adminJobLoading !== null}
              style={{
                padding: "8px 12px",
                background: adminJobLoading !== null ? "#ccc" : "#424242",
                color: "white",
                border: "none",
                borderRadius: 4,
                cursor: adminJobLoading !== null ? "not-allowed" : "pointer",
                fontWeight: 500
              }}
            >
              {adminJobLoading === "fakturownia" ? "⏳ Uruchamiam..." : "📋 Zaktualizuj bazę klientów"}
            </button>
          </div>
        </div>
      </div>
      {adminJobOutput && (
        <div style={{ marginBottom: 24, padding: 16, border: "1px solid #4CAF50", borderRadius: 6, background: "#f1f8f4" }}>
          <h4 style={{ marginTop: 0, color: "#2e7d32" }}>✅ Wynik zadania</h4>
          <pre style={{ background: "#fff", padding: 12, borderRadius: 4, overflow: "auto", fontSize: 13, margin: 0 }}>
            {adminJobOutput}
          </pre>
        </div>
      )}
      {adminJobError && (
        <div style={{ marginBottom: 24, padding: 16, border: "1px solid #f44336", borderRadius: 6, background: "#ffebee" }}>
          <h4 style={{ marginTop: 0, color: "#c62828" }}>❌ Błąd zadania</h4>
          <div style={{ color: "#c62828", fontWeight: 500 }}>{adminJobError}</div>
        </div>
      )}

      {/* Tabela użytkowników */}
      <div style={{ marginTop: 32 }}>
        <h3 style={{ marginBottom: 16 }}>👥 Zarządzanie użytkownikami</h3>
        {loading && <div style={{ padding: 16, textAlign: "center", color: "#666" }}>⏳ Ładowanie użytkowników...</div>}
        {!loading && (
          <table style={{
            width: "100%",
            borderCollapse: "collapse",
            border: "1px solid #ddd",
            borderRadius: 6,
            overflow: "hidden",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
          }}>
            <thead>
              <tr style={{ background: "#f5f5f5" }}>
                <th style={{ padding: 12, textAlign: "left", borderBottom: "2px solid #ddd", fontWeight: 600 }}>ID</th>
                <th style={{ padding: 12, textAlign: "left", borderBottom: "2px solid #ddd", fontWeight: 600 }}>Email</th>
                <th style={{ padding: 12, textAlign: "left", borderBottom: "2px solid #ddd", fontWeight: 600 }}>Rola</th>
                <th style={{ padding: 12, textAlign: "left", borderBottom: "2px solid #ddd", fontWeight: 600 }}>Data utworzenia</th>
                <th style={{ padding: 12, textAlign: "center", borderBottom: "2px solid #ddd", fontWeight: 600 }}>Status</th>
                <th style={{ padding: 12, textAlign: "center", borderBottom: "2px solid #ddd", fontWeight: 600 }}>Akcje</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={{ borderBottom: "1px solid #eee" }}>
                  <td style={{ padding: 12 }}>{u.id}</td>
                  <td style={{ padding: 12, fontWeight: 500 }}>{u.email}</td>
                  <td style={{ padding: 12 }}>
                    <span style={{
                      padding: "4px 8px",
                      borderRadius: 4,
                      fontSize: 12,
                      fontWeight: 600,
                      background: u.role === "admin" ? "#e3f2fd" : "#f5f5f5",
                      color: u.role === "admin" ? "#1565c0" : "#666"
                    }}>
                      {u.role === "admin" ? "👑 Admin" : "👤 User"}
                    </span>
                  </td>
                  <td style={{ padding: 12, color: "#666" }}>{u.created_at?.split("T")[0]}</td>
                  <td style={{ padding: 12, textAlign: "center" }}>
                    <span style={{
                      padding: "4px 10px",
                      borderRadius: 12,
                      fontSize: 12,
                      fontWeight: 600,
                      background: u.is_active ? "#e8f5e9" : "#ffebee",
                      color: u.is_active ? "#2e7d32" : "#c62828"
                    }}>
                      {u.is_active ? "● Aktywny" : "○ Nieaktywny"}
                    </span>
                  </td>
                  <td style={{ padding: 12, textAlign: "center" }}>
                    <button
                      onClick={() => handleToggleActive(u)}
                      style={{
                        padding: "6px 12px",
                        marginRight: 6,
                        border: "1px solid #ccc",
                        borderRadius: 4,
                        background: "white",
                        cursor: "pointer",
                        fontSize: 13
                      }}
                    >
                      {u.is_active ? "Dezaktywuj" : "Aktywuj"}
                    </button>
                    <button
                      onClick={() => handleToggleRole(u)}
                      style={{
                        padding: "6px 12px",
                        marginRight: 6,
                        border: "1px solid #2196F3",
                        borderRadius: 4,
                        background: u.role === "admin" ? "#e3f2fd" : "#2196F3",
                        color: u.role === "admin" ? "#1565c0" : "white",
                        cursor: "pointer",
                        fontSize: 13,
                        fontWeight: 500
                      }}
                    >
                      {u.role === "admin" ? "Odbierz admin" : "Nadaj admin"}
                    </button>
                    <button
                      onClick={() => handleDelete(u)}
                      style={{
                        padding: "6px 12px",
                        border: "1px solid #f44336",
                        borderRadius: 4,
                        background: "#ffebee",
                        color: "#c62828",
                        cursor: "pointer",
                        fontSize: 13,
                        fontWeight: 500
                      }}
                    >
                      🗑️ Usuń
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default DashboardAdmin;
